
import { Component, OnInit, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IframelyFusionType } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FusionService } from '@shared-angular/services/fusion.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { capitalizeOrParse, getFusionBotTypeOptions } from '../fusionbot-tab.utils';
import { PlaceholderInfoDirective } from 'src/app/shared/directives/placeholder-info.directive';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { CodemirrorHtmlComponent } from '../../../../../../../../shared/components/ai-bot/utils/codemirror-html/codemirror-html.component';

@Component({
  selector: 'app-fusion-iframely-config',
  imports: [FormsModule, ButtonModule, InputTextModule, SelectModule, ToggleSwitchModule, RadioButtonModule, SelectButtonModule, PlaceholderInfoDirective, GotoBlocklyDirective, CodemirrorHtmlComponent],
  templateUrl: './fusion-iframely-config.component.html',
  styleUrl: './fusion-iframely-config.component.scss'
})
export class FusionIframelyConfigComponent implements OnInit {
  public readonly iframelyType = input.required<string>();
  public safeHtml?: SafeHtml;
  public capitalizeOrParse = capitalizeOrParse;
  public showHtmlEditor = false;
  protected get botTypeOptions() {
    return getFusionBotTypeOptions(this.fusionService.loginService.systemConf);
  }
  protected systemLanguageOptions = [
    { label: $localize`:@@fusionconfig-systemlang-de:Deutsch`, value: 'de' },
    { label: $localize`:@@fusionconfig-systemlang-en:Englisch`, value: 'en' },
    { label: $localize`:@@fusionconfig-systemlang-fr:Französisch`, value: 'fr' },
    { label: $localize`:@@fusionconfig-systemlang-es:Spanisch`, value: 'es' },
    { label: $localize`:@@fusionconfig-systemlang-it:Italienisch`, value: 'it' },
    { label: $localize`:@@fusionconfig-systemlang-nl:Niederländisch`, value: 'nl' },
    { label: $localize`:@@fusionconfig-systemlang-nb:Norwegisch`, value: 'nb-no' },
    { label: $localize`:@@fusionconfig-systemlang-fr-be:Französisch (Belgien)`, value: 'fr-be' },
    { label: $localize`:@@fusionconfig-systemlang-nl-be:Niederländisch (Belgien)`, value: 'nl-be' },
    { label: $localize`:@@fusionconfig-systemlang-pl:Polnisch`, value: 'pl' },
    { label: $localize`:@@fusionconfig-systemlang-pt:Portugiesisch`, value: 'pt' },
    { label: $localize`:@@fusionconfig-systemlang-hu:Ungarisch`, value: 'hu' },
    { label: $localize`:@@fusionconfig-systemlang-ro:Rumänisch`, value: 'ro' },
    { label: $localize`:@@fusionconfig-systemlang-tr:Türkisch`, value: 'tr' },
    { label: $localize`:@@fusionconfig-systemlang-fi:Finnisch`, value: 'fi' },
    { label: $localize`:@@fusionconfig-systemlang-sv:Schwedisch`, value: 'sv' },
    { label: $localize`:@@fusionconfig-systemlang-da:Dänisch`, value: 'da' },
    { label: $localize`:@@fusionconfig-systemlang-cs:Tschechisch`, value: 'cs' },
    { label: $localize`:@@fusionconfig-systemlang-el:Griechisch`, value: 'el' }
  ];
  protected viewModeOptions = [
    { label: $localize`:@@fusionconfig-viewmode-split:Split (Side-by-Side)`, value: 'split' },
    { label: $localize`:@@fusionconfig-viewmode-split-reverse:Split Reverse (Side-by-Side)`, value: 'split-reverse' },
    { label: $localize`:@@fusionconfig-viewmode-window:Fenster (Overlay)`, value: 'window' },
    { label: $localize`:@@fusionconfig-viewmode-full:Vollbild`, value: 'full' },
    { label: $localize`:@@fusionconfig-viewmode-float:Floating (Chat-Bubble)`, value: 'float' }
  ];

  constructor(public fusionService: FusionService,
    public rss: RouteShareService,
    public langService: LanguageService,
    public sanitizer: DomSanitizer,
    public toastr: ToastrService,
    public editorItemService: EditorItemsService
  ) { }

  public ngOnInit(): void {
    if (this.fusionService.selectedIframely && this.fusionService.selectedIframely.metadata.html !== '') {
      this.updateSafeHtml(this.fusionService.selectedIframely.metadata.html);
    }
  }

  private updateSafeHtml(html: string) {
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public async addIframely(url: string) {
    if (!this.fusionService.selectedFusionContent) { return; }

    if (!url.toLowerCase().includes(this.fusionService.selectedFusionContent.metadata.type.toLowerCase().replace("iframely_", ""))) {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain-for:Domain ist ungültig für`) + ` ${capitalizeOrParse(this.fusionService.selectedFusionContent.metadata.type as IframelyFusionType)}`);
      return;
    }
    const result = await this.fusionService.embedInfo(url);
    if (!result.info.html || result.info.html == "null") {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain:URL ist ungültig`));
      return;
    }
    await this.fusionService.updateFusionField("iframelyType", this.fusionService.selectedFusionContent.metadata.type.replace("iframely_", ""));
    await this.fusionService.updateFusionField("url", result.info.url);
    await this.fusionService.updateFusionField("html", result.info.html);
    this.updateSafeHtml(result.info.html);
  }
}
